import { Button, FontAwesome, Badge } from 'Elements';
import { FC, useEffect, useState } from 'react';

import { Colors } from 'src/types/Color';
import styles from './locations.module.scss';
import { useTranslation } from 'react-i18next';

import Drawer from 'react-modern-drawer';
import InfiniteScroll from 'react-infinite-scroller';

import {
	clx,
	debounce,
	formatLocalAddress,
	isAddressMatch
} from 'src/helpers/Utils';
import { Branch } from 'src/types/BusinessPartner';
import {
	useGetB2bUserProfileQuery,
	useCommunityPartnerMeQuery,
	useBusinessPartnerMeQuery
} from 'src/hooks/useUser';

interface Props {
	isOpen?: boolean;
	isLoading: boolean;
	data: Branch[];
	hasNextPage: boolean;
	isFetchingNextPage: boolean;
	fetchNextPage: () => void;
	keyword: string;
	setKeyword: (keyword: string) => void;
	locations?: [
		{
			postalCode: string;
			address: string;
			primary: boolean;
			regionCode: number;
			title: string;
			locationPoint: {
				longitude: number;
				latitude: number;
			};
		}
	];
	onAdd?: (arr: any) => void;
	toggleDrawer?: () => void;
}

export const LocationListDrawer: FC<Props> = ({
	isOpen,
	data,
	isLoading,
	toggleDrawer,
	locations,
	onAdd,
	hasNextPage,
	isFetchingNextPage,
	fetchNextPage,
	keyword,
	setKeyword
}) => {
	const { t } = useTranslation();
	const [locationsItem, setLocationItem] = useState<
		(Branch & { active?: boolean })[]
	>([]);
	const { data: user } = useGetB2bUserProfileQuery();
	const { data: communityPartner } = useCommunityPartnerMeQuery({
		enabled: user?.partnerType === 1
	});
	const { data: businessPartner } = useBusinessPartnerMeQuery({
		enabled: user?.partnerType === 2
	});

	useEffect(() => {
		setLocationItem(data || []);
	}, [user, data]);

	// Extract primary location from partner data
	const getPrimaryLocation = () => {
		const partnerData =
			user?.partnerType === 1 ? communityPartner : businessPartner;
		if (!partnerData?.address) return null;

		return {
			postalCode: partnerData.address.postalCode,
			address: formatLocalAddress(
				partnerData.address.street,
				partnerData.address.houseNumber,
				partnerData.address.postalCode,
				partnerData.address.city,
				partnerData.address.state
			),
			primary: true,
			regionCode: Number(partnerData.regionCode),
			title: partnerData.address.city || partnerData.businessName,
			name: partnerData.address.city,
			locationPoint: {
				longitude: partnerData.address.location?.longitude || 0,
				latitude: partnerData.address.location?.latitude || 0
			}
		};
	};

	// Check if primary location is already in the list
	const isPrimaryLocationInList = () => {
		const primaryLoc = getPrimaryLocation();
		if (!primaryLoc) return true;

		return locations?.some((location) => location.primary) ?? false;
	};

	const filteredLocations = locationsItem.filter((item) => {
		if (!keyword) return true;

		const lowerKeyword = keyword.toLowerCase();
		const name = item.name?.toLowerCase() || '';
		const city = item.address?.city?.toLowerCase() || '';
		const postalCode = item.address?.postalCode?.toLowerCase() || '';
		const street = item.address?.street?.toLowerCase() || '';

		return (
			name.includes(lowerKeyword) ||
			city.includes(lowerKeyword) ||
			postalCode.includes(lowerKeyword) ||
			street.includes(lowerKeyword)
		);
	});

	const isLocationAlreadyAdded = (item: Branch) => {
		return (
			locations?.some((location) => {
				return isAddressMatch(location.address, {
					street: item?.address?.street,
					houseNumber: item?.address?.houseNumber,
					postalCode: item?.address?.postalCode
				});
			}) ?? false
		);
	};

	const handleAddSingleLocation = (item) => {
		const locationToAdd = {
			postalCode: item?.address?.postalCode ?? item?.postalCode,
			address: item?.address?.street
				? formatLocalAddress(
						item?.address?.street,
						item?.address?.houseNumber,
						item?.address?.postalCode,
						item?.address?.city,
						item?.address?.state
				  )
				: item?.address,
			primary: item?.primary ?? false,
			regionCode: item?.regionCode,
			title: item?.address?.city ?? item?.name,
			name: item?.name,
			locationPoint: {
				longitude:
					item?.address?.location?.longitude ??
					item?.locationPoint?.longitude,
				latitude:
					item?.address?.location?.latitude ??
					item?.locationPoint?.latitude
			}
		};
		onAdd && onAdd([locationToAdd]);
	};
	const handleSearchChange = debounce(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setKeyword(e.target.value);
		},
		600
	);

	return (
		<Drawer
			open={isOpen as never}
			onClose={() => {
				toggleDrawer && toggleDrawer();
			}}
			lockBackgroundScroll
			zIndex={100}
			direction="right"
			className="d-flex flex-column gap-3"
			size="34rem">
			<div className="d-flex justify-content-between align-items-center px-4 py-4 border-bottom border-gray-1 border">
				<h2 className="fs-5 fw-600 ">
					{t('text.available-locations')}
				</h2>
				<Button
					size="sm"
					color={Colors['white-gray']}
					className="gap-2"
					onClick={() => {
						toggleDrawer && toggleDrawer();
					}}>
					<FontAwesome icon="close" />
					{t('button.close')}
				</Button>
			</div>
			<div className="px-4">
				<div className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 mb-1">
					<div className="p-0 inputbox">
						<input
							type="text"
							className={clx(styles.inputSearch, 'w-100')}
							placeholder={t('menu.search')}
							onChange={handleSearchChange}
						/>
						<Button
							type="button"
							color={Colors.empty}
							className={clx('p-0', styles.submitSearch)}>
							<FontAwesome size="sm" icon={'magnifying-glass'} />
						</Button>
					</div>
				</div>
			</div>
			<div
				className="d-flex flex-column border-top border-gray-1 gap-3 px-4 py-3"
				style={{ overflowY: 'auto' }}>
				{/* Primary Location Card */}
				{!isPrimaryLocationInList() && getPrimaryLocation() && (
					<div className={styles.CardBranch}>
						<div className="d-flex align-items-start justify-content-between gap-3">
							<div className="d-flex flex-column gap-3 flex-grow-1">
								<span className="fs-base fw-500">
									{getPrimaryLocation()?.name}
								</span>
								<div>
									<Badge color={Colors.primary}>
										{t('text.primary')}
									</Badge>
								</div>
								<div className="d-flex gap-2">
									<FontAwesome
										icon="location-dot"
										size="sm"
										color="text-primary"
									/>
									{getPrimaryLocation()?.address}
								</div>
								<Button
									size="sm"
									color={Colors.primary}
									className="gap-2 px-5"
									onClick={() => {
										const primaryLoc = getPrimaryLocation();
										if (primaryLoc) {
											handleAddSingleLocation(primaryLoc);
										}
									}}>
									{t('button.add')}
								</Button>
							</div>
						</div>
					</div>
				)}
				<InfiniteScroll
					hasMore={hasNextPage && !isLoading && !isFetchingNextPage}
					useWindow={false}
					className="d-flex flex-column gap-3 w-100"
					loader={
						<div
							key="loader"
							className={styles.cardBranchSkeleton}></div>
					}
					loadMore={() => {
						if (!isFetchingNextPage) {
							fetchNextPage();
						}
					}}>
					{!isLoading &&
						filteredLocations
							?.filter((item) => !isLocationAlreadyAdded(item))
							.map((item) => {
								return (
									<div
										key={item.id}
										className={styles.CardBranch}>
										<div className="d-flex align-items-start justify-content-between gap-3">
											<div className="d-flex flex-column gap-3 flex-grow-1">
												<span className="fs-base fw-500">
													{item.name}
												</span>
												<div>
													<Badge
														color={
															item.active
																? Colors.primary
																: Colors.muted
														}>
														{'primary' in item &&
														item.primary
															? t('text.primary')
															: item.active
															? t('label.active')
															: t(
																	'label.inactive'
															  )}
													</Badge>
												</div>
												<div className="d-flex gap-2">
													<FontAwesome
														icon="location-dot"
														size="sm"
														color="text-primary"
													/>
													{formatLocalAddress(
														item?.address?.street,
														item?.address
															?.houseNumber,
														item?.address
															?.postalCode,
														item?.address?.city,
														item?.address
															?.state as any
													)}
												</div>
												<div
													className={clx(
														'text-gray-3 fs-sm fw-400 mt-1',
														styles.description
													)}
													style={{
														whiteSpace: 'pre-wrap'
													}}
													dangerouslySetInnerHTML={{
														__html:
															item.shortDescription ??
															''
													}}
												/>
												{item.active && (
													<Button
														size="sm"
														color={Colors.primary}
														className="gap-2 px-5"
														onClick={() =>
															handleAddSingleLocation(
																item
															)
														}>
														{t('button.add')}
													</Button>
												)}
											</div>
										</div>
									</div>
								);
							})}
					{isLoading &&
						[1, 2, 3].map((item) => {
							return (
								<div
									key={item}
									className={styles.cardBranchSkeleton}></div>
							);
						})}
				</InfiniteScroll>
			</div>
		</Drawer>
	);
};
